import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { assessmentQuestions } from '../data/questions';
import { AssessmentService } from '../services/assessmentService';

const { FiChevronRight, FiChevronLeft, FiCheck, FiUser, FiMail, FiTarget, FiDatabase, FiWifi } = FiIcons;

const Assessment = ({ onComplete }) => {
  const navigate = useNavigate();
  const [currentSection, setCurrentSection] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isStarted, setIsStarted] = useState(false);
  const [userInfo, setUserInfo] = useState({ name: '', email: '' });
  const [showUserForm, setShowUserForm] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Check Supabase connection on component mount
  useEffect(() => {
    const checkConnection = async () => {
      const result = await AssessmentService.checkDatabaseConnection();
      setIsConnected(result.success);
      if (!result.success) {
        console.warn('Supabase connection failed:', result.error);
      }
    };
    checkConnection();
  }, []);

  const sections = Object.keys(assessmentQuestions);
  const currentSectionData = assessmentQuestions[sections[currentSection]];
  const totalQuestions = Object.values(assessmentQuestions).reduce(
    (sum, section) => sum + section.questions.length,
    0
  );
  const answeredQuestions = Object.keys(answers).length;

  const handleUserInfoChange = (field, value) => {
    setUserInfo(prev => ({ ...prev, [field]: value }));
  };

  const handleStartAssessment = () => {
    if (userInfo.name.trim() && userInfo.email.trim()) {
      setIsStarted(true);
      setShowUserForm(false);
    }
  };

  const handleAnswer = (questionId, score) => {
    setAnswers(prev => ({ ...prev, [questionId]: score }));
  };

  const handleNext = () => {
    if (currentQuestion < currentSectionData.questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else if (currentSection < sections.length - 1) {
      setCurrentSection(currentSection + 1);
      setCurrentQuestion(0);
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    } else if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
      setCurrentQuestion(assessmentQuestions[sections[currentSection - 1]].questions.length - 1);
    }
  };

  const handleComplete = async () => {
    setIsSaving(true);
    
    const totalScore = Object.values(answers).reduce((sum, score) => sum + score, 0);
    const sectionScores = {};
    
    sections.forEach(sectionKey => {
      const sectionQuestions = assessmentQuestions[sectionKey].questions;
      const sectionScore = sectionQuestions.reduce((sum, q) => sum + (answers[q.id] || 0), 0);
      sectionScores[sectionKey] = {
        score: sectionScore,
        maxScore: sectionQuestions.length * 3,
        percentage: Math.round((sectionScore / (sectionQuestions.length * 3)) * 100)
      };
    });

    const assessmentData = {
      userInfo,
      answers,
      totalScore,
      maxScore: 54, // Total possible score for 18 questions
      percentage: Math.round((totalScore / 54) * 100),
      sectionScores,
      completedAt: new Date().toISOString()
    };

    // Save to Supabase if connected
    if (isConnected) {
      try {
        const result = await AssessmentService.saveAssessment(assessmentData);
        if (result.success) {
          console.log('✅ Assessment saved successfully:', result.data);
          assessmentData.savedToDatabase = true;
          assessmentData.databaseId = result.data.id;
        } else {
          console.error('❌ Failed to save assessment:', result.error);
          assessmentData.savedToDatabase = false;
        }
      } catch (error) {
        console.error('❌ Error saving assessment:', error);
        assessmentData.savedToDatabase = false;
      }
    } else {
      assessmentData.savedToDatabase = false;
    }

    setIsSaving(false);
    onComplete(assessmentData);
    navigate('/results');
  };

  const currentQuestionData = currentSectionData.questions[currentQuestion];
  const currentQuestionId = currentQuestionData.id;
  const isAnswered = Object.prototype.hasOwnProperty.call(answers, currentQuestionId);
  const isLastQuestion = currentSection === sections.length - 1 && currentQuestion === currentSectionData.questions.length - 1;

  // Connection Status Indicator
  const ConnectionStatus = () => (
    <div className="fixed top-4 right-4 z-50">
      <div className={`flex items-center px-3 py-2 rounded-full text-sm font-medium ${
        isConnected 
          ? 'bg-sage-100 text-sage-700 border border-sage-200' 
          : 'bg-red-100 text-red-700 border border-red-200'
      }`}>
        <SafeIcon 
          icon={isConnected ? FiDatabase : FiWifi} 
          className="mr-2 text-xs" 
        />
        {isConnected ? 'Connected' : 'Offline Mode'}
      </div>
    </div>
  );

  // User Info Collection Form
  if (showUserForm) {
    return (
      <>
        <ConnectionStatus />
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-lg mx-auto"
        >
          <div className="bg-bg-primary rounded-2xl ernest-shadow p-8">
            <div className="text-center mb-8">
              <div className="ernest-gradient p-4 rounded-xl inline-flex mb-4 ernest-hover">
                <SafeIcon icon={FiUser} className="text-white text-2xl" />
              </div>
              <h2 className="text-2xl font-bold text-text-primary mb-2">
                Let's Get Started
              </h2>
              <p className="text-text-secondary">
                Please provide your details to personalize your assessment results.
              </p>
              {isConnected && (
                <div className="mt-4 p-3 bg-sage-50 border border-sage-200 rounded-lg">
                  <p className="text-sm text-sage-700">
                    ✅ Your results will be saved securely for future reference
                  </p>
                </div>
              )}
            </div>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Full Name
                </label>
                <div className="relative">
                  <SafeIcon 
                    icon={FiUser} 
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-secondary" 
                  />
                  <input
                    type="text"
                    value={userInfo.name}
                    onChange={(e) => handleUserInfoChange('name', e.target.value)}
                    placeholder="Enter your full name"
                    className="w-full pl-10 pr-4 py-3 border-2 border-gray-1 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-bg-primary transition-all"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <SafeIcon 
                    icon={FiMail} 
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-secondary" 
                  />
                  <input
                    type="email"
                    value={userInfo.email}
                    onChange={(e) => handleUserInfoChange('email', e.target.value)}
                    placeholder="Enter your email address"
                    className="w-full pl-10 pr-4 py-3 border-2 border-gray-1 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-bg-primary transition-all"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-4 mt-8">
              <button
                onClick={() => setShowUserForm(false)}
                className="flex-1 py-3 px-6 border-2 border-gray-1 text-text-secondary rounded-xl font-medium hover:bg-bg-light transition-all"
              >
                Back
              </button>
              <button
                onClick={handleStartAssessment}
                disabled={!userInfo.name.trim() || !userInfo.email.trim()}
                className="flex-1 py-3 px-6 bg-secondary-500 text-white rounded-xl font-semibold hover:bg-secondary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 ernest-hover"
              >
                Start Assessment
              </button>
            </div>
          </div>
        </motion.div>
      </>
    );
  }

  // Initial Welcome Screen
  if (!isStarted) {
    return (
      <>
        <ConnectionStatus />
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-2xl mx-auto"
        >
          <div className="bg-bg-primary rounded-2xl ernest-shadow p-8">
            <div className="text-center mb-8">
              <div className="ernest-gradient p-6 rounded-xl inline-flex mb-6 ernest-hover">
                <SafeIcon icon={FiTarget} className="text-white text-4xl" />
              </div>
              <h2 className="text-3xl font-bold text-text-primary mb-4">
                Bridge Framework™ Assessment
              </h2>
              <h3 className="text-xl font-semibold text-primary-600 mb-4">
                Discover Your Coaching Needs
              </h3>
              <p className="text-lg text-text-secondary leading-relaxed">
                This assessment will help identify areas where the Bridge Framework™ coaching system 
                could support your personal and professional growth. Take the first step toward 
                bridging the gap between intention and achievement.
              </p>
            </div>

            <div className="bg-bg-cream border border-accent/20 rounded-xl p-6 mb-8">
              <h3 className="text-lg font-semibold text-text-primary mb-4">Assessment Details:</h3>
              <ul className="space-y-3 text-text-secondary">
                <li className="flex items-center">
                  <SafeIcon icon={FiCheck} className="text-primary-500 mr-3 flex-shrink-0" />
                  <span>18 questions across {sections.length} key areas</span>
                </li>
                <li className="flex items-center">
                  <SafeIcon icon={FiCheck} className="text-primary-500 mr-3 flex-shrink-0" />
                  <span>Takes approximately 10-15 minutes</span>
                </li>
                <li className="flex items-center">
                  <SafeIcon icon={FiCheck} className="text-primary-500 mr-3 flex-shrink-0" />
                  <span>Immediate personalized results and recommendations</span>
                </li>
                <li className="flex items-center">
                  <SafeIcon icon={FiCheck} className="text-primary-500 mr-3 flex-shrink-0" />
                  <span>Detailed Bridge Framework™ phase analysis</span>
                </li>
                {isConnected && (
                  <li className="flex items-center">
                    <SafeIcon icon={FiDatabase} className="text-sage-500 mr-3 flex-shrink-0" />
                    <span>Results saved securely for future reference</span>
                  </li>
                )}
              </ul>
            </div>

            <button
              onClick={() => setShowUserForm(true)}
              className="w-full bg-secondary-500 text-white py-4 px-8 rounded-xl font-semibold text-lg hover:bg-secondary-600 transition-all duration-300 ernest-hover ernest-shadow"
            >
              Begin Assessment
            </button>
          </div>
        </motion.div>
      </>
    );
  }

  // Assessment Questions
  return (
    <>
      <ConnectionStatus />
      <motion.div
        key={`${currentSection}-${currentQuestion}`}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        className="max-w-3xl mx-auto"
      >
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-text-secondary">
              Question {answeredQuestions + 1} of {totalQuestions}
            </span>
            <span className="text-sm font-medium text-text-secondary">
              {Math.round(((answeredQuestions) / totalQuestions) * 100)}% Complete
            </span>
          </div>
          <div className="w-full bg-gray-1 rounded-full h-3">
            <motion.div
              className="ernest-gradient h-3 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${(answeredQuestions / totalQuestions) * 100}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
        </div>

        {/* User Info Display */}
        <div className="bg-primary-50 border-2 border-primary-200 rounded-xl p-4 mb-6">
          <div className="flex items-center text-sm text-primary-700">
            <SafeIcon icon={FiUser} className="mr-2" />
            <span className="font-medium">{userInfo.name}</span>
            <span className="mx-2">•</span>
            <span>{userInfo.email}</span>
            {isConnected && (
              <>
                <span className="mx-2">•</span>
                <SafeIcon icon={FiDatabase} className="mr-1" />
                <span>Saving Progress</span>
              </>
            )}
          </div>
        </div>

        {/* Question Card */}
        <div className="bg-bg-primary rounded-2xl ernest-shadow p-8">
          <div className="mb-6">
            <div className="text-sm font-medium text-primary-600 mb-2">
              {currentSectionData.title}
            </div>
            <h2 className="text-2xl font-bold text-text-primary leading-relaxed">
              {currentQuestionData.question}
            </h2>
          </div>

          <div className="space-y-3">
            {currentQuestionData.options.map((option, index) => (
              <motion.button
                key={index}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                onClick={() => handleAnswer(currentQuestionId, option.score)}
                className={`w-full p-4 text-left rounded-xl border-2 transition-all duration-300 ${
                  answers[currentQuestionId] === option.score
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-gray-1 hover:border-sage-300 hover:bg-bg-light'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium">{option.text}</span>
                  <div
                    className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                      answers[currentQuestionId] === option.score
                        ? 'border-primary-500 bg-primary-500'
                        : 'border-gray-2'
                    }`}
                  >
                    {answers[currentQuestionId] === option.score && (
                      <SafeIcon icon={FiCheck} className="text-white text-sm" />
                    )}
                  </div>
                </div>
              </motion.button>
            ))}
          </div>

          {/* Navigation */}
          <div className="flex justify-between items-center mt-8">
            <button
              onClick={handlePrevious}
              disabled={currentSection === 0 && currentQuestion === 0}
              className="flex items-center px-6 py-3 text-text-secondary hover:text-text-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <SafeIcon icon={FiChevronLeft} className="mr-2" />
              Previous
            </button>

            <button
              onClick={handleNext}
              disabled={!isAnswered || isSaving}
              className="flex items-center px-8 py-3 bg-secondary-500 text-white rounded-xl font-semibold hover:bg-secondary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 ernest-hover"
            >
              {isSaving ? (
                'Saving...'
              ) : isLastQuestion ? (
                'Complete Assessment'
              ) : (
                'Next'
              )}
              {!isLastQuestion && !isSaving && (
                <SafeIcon icon={FiChevronRight} className="ml-2" />
              )}
            </button>
          </div>
        </div>
      </motion.div>
    </>
  );
};

export default Assessment;